// src/components/ModalProfesionalDetail.js
import React, { useRef, useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchProfessionalById } from '../api/queries';
import { useNavigate } from 'react-router-dom';
import ActionButton from '../components/ActionButton';
import './ModalProfesionalDetail.css';
import { IoPeopleOutline } from 'react-icons/io5';
import { PiPhone } from 'react-icons/pi';
import { FaWhatsapp } from 'react-icons/fa';
import { HiOutlineMail } from 'react-icons/hi';
import { TfiAlarmClock } from 'react-icons/tfi';
import { SlLocationPin } from "react-icons/sl";

export default function ModalProfesionalDetail({ professionalId }) {
  // 1) Datos del profesional
  const { data: profData, isLoading, isError, error } = useQuery({
    queryKey: ['professional', professionalId],
    queryFn: () => fetchProfessionalById(professionalId),
    staleTime: 5 * 60_000,
    cacheTime: 30 * 60_000,
  });

  // 2) React-Query blob-cache para la imagen
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // 2) Prefetch de la imagen de cabecera (solo la primera vez)
  useEffect(() => {
    if (profData?.cabecera) {
      queryClient.prefetchQuery({
        queryKey: ['profImage', profData.cabecera],
        queryFn: async () => {
          const res = await fetch(profData.cabecera);
          const blob = await res.blob();
          return URL.createObjectURL(blob);
        },
        staleTime: 24 * 60 * 60_000,
        cacheTime: 24 * 60 * 60_000,
      });
    }
  }, [profData?.cabecera, queryClient]);

  // 3) useQuery que primero intentará servir de cache, sin volver a fetchear
  const { data: blobUrl } = useQuery({
    queryKey: ['profImage', profData?.cabecera],
    queryFn: async () => {
      const res = await fetch(profData.cabecera);
      const blob = await res.blob();
      return URL.createObjectURL(blob);
    },
    enabled: !!profData?.cabecera,
    staleTime: 24 * 60 * 60_000,
    cacheTime: 24 * 60 * 60_000,
    // Gracias a initialData, si ya prefetchQuery guardó el blob,
    // aquí se recupera de cache sin volver a ejecutar queryFn
    initialData: () => queryClient.getQueryData(['profImage', profData?.cabecera]),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  // 4) Refs y estados para recorte con CSS
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const [imgStyle, setImgStyle] = useState({
    position: 'absolute',
    width: '100%',
    height: '100%',
    left: '0',
    top: '0',
    objectFit: 'cover'
  });
  // Si ya tenemos blobUrl en cache, partimos de ready=
  const initialReady = !!queryClient.getQueryData(['profImage', profData?.cabecera]);
  const [ready, setReady] = useState(initialReady);

  // Forzamos manualmente la onLoad si el blob ya venía de cache
  useEffect(() => {
    if (blobUrl && imgRef.current) {
      const w = imgRef.current.naturalWidth;
      const h = imgRef.current.naturalHeight;
      console.log('Manual onLoad for blobUrl, natural size:', { w, h });
      setNaturalSize({ width: w, height: h });
      setReady(true);
    }
  }, [blobUrl]);

  // Recalculamos el estilo de recorte cuando cambian crop o tamaño natural
  useEffect(() => {
    const cropRect = profData?.cabecera_crop?.croppedAreaPixels || {
      x: 0,
      y: 0,
      width: naturalSize.width,
      height: naturalSize.height
    };
    const cont = containerRef.current;
    if (!blobUrl || !cont || naturalSize.width === 0) {
      setImgStyle({
        position: 'absolute',
        width: '100%',
        height: '100%',
        left: '0',
        top: '0',
        objectFit: 'cover'
      });
      return;
    }

    const contW = cont.clientWidth;
    const contH = (contW * 2) / 5;
    const scale = contW / cropRect.width;
    const scaledW = naturalSize.width * scale;
    const scaledH = naturalSize.height * scale;
    const left = scaledW < contW
      ? (contW - scaledW) / 2
      : -cropRect.x * scale;
    const top = scaledH < contH
      ? (contH - scaledH) / 2
      : -cropRect.y * scale;

    setImgStyle({
      position: 'absolute',
      width: `${scaledW}px`,
      height: `${scaledH}px`,
      left: `${left}px`,
      top: `${top}px`,
      objectFit: 'cover'
    });
  }, [blobUrl, naturalSize, profData?.cabecera_crop?.croppedAreaPixels]);

  // Captura tamaño natural al cargar la imagen
  const handleImgLoad = () => {
    if (!imgRef.current) return;
    const w = imgRef.current.naturalWidth;
    const h = imgRef.current.naturalHeight;
    console.log('Img loaded natural size:', { w, h });
    setNaturalSize({ width: w, height: h });
    setReady(true);
  };

  // Sticky buttons…
  const buttonContainerRef = useRef(null);
  const [isStuck] = useState(false);

  const handleButtonClick = () => {
    if (!isStuck && buttonContainerRef.current) {
      buttonContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // 6) Navegación y cierre
  const handleClose = () => navigate(-1);

  // Estados de carga y error
  if (isLoading) {
    return (
      <div className="modalProfOverlay" onClick={handleClose}>
        <div className="modalProfCard" onClick={e => e.stopPropagation()}>
          <p>Cargando profesional...</p>
        </div>
      </div>
    );
  }
  if (isError) {
    console.error('[ModalProfesionalDetail] Error:', error);
    return (
      <div className="modalProfOverlay" onClick={handleClose}>
        <div className="modalProfCard" onClick={e => e.stopPropagation()}>
          <p>Error al cargar profesional.</p>
        </div>
      </div>
    );
  }

  console.log('Rendering ModalProfesionalDetail with ready:', ready, 'blobUrl:', blobUrl);

  return (
    <div className="modalProfOverlay" onClick={handleClose}>
      <div className="modalProfCard" onClick={e => e.stopPropagation()}>

        {/* Cabecera: placeholder gris hasta ready */}
        <div
          className="modalProfHeaderImage"
          ref={containerRef}
          style={{
            // Siempre gris hasta que ready === true
            background: ready ? 'transparent' : '#f0f0f0'
          }}
        >
          {blobUrl && (
            <img
              key={`${blobUrl}`}                // fuerza remount si cambian coords
              ref={imgRef}
              src={blobUrl}
              alt="Cabecera profesional"
              onLoad={handleImgLoad}
              style={{
                ...imgStyle,
                // Oculto hasta que handleImgLoad marque ready=true
                visibility: ready ? 'visible' : 'hidden'
              }}
            />
          )}
        </div>

        {/* Divisor */}
        <div className="modalProfHeaderDivider" />

        {/* Información principal */}
        <div className="modalProfInfoTop">
          {profData.tipo && (
            <p className="icons-info">
              <IoPeopleOutline className="icon" />
              <span className="info-text">{profData.tipo}</span>
            </p>
          )}
          <div className="contact-info">
            {profData.telefono && (
              <div className="icons-info">
                <PiPhone className="icon" />
                <span className="info-text">
                  <a href={`tel:${profData.telefono}`}>{profData.telefono}</a>
                </span>
              </div>
            )}
            {profData.whatsapp && (
              <div className="icons-info">
                <FaWhatsapp className="icon" />
                <span className="info-text">
                  <a
                    href={`https://wa.me/34${profData.whatsapp}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {profData.whatsapp}
                  </a>
                </span>
              </div>
            )}
          </div>
          {profData.email && (
            <p className="icons-info">
              <HiOutlineMail className="icon" />
              <span className="info-text">
                <a href={`mailto:${profData.email}`}>{profData.email}</a>
              </span>
            </p>
          )}
          {profData.direccion && (
            <a
              className="icons-info info-link"
              href={
                // primero comprobamos coords con optional chaining
                profData.ubicacion_geografica?.coordinates?.length === 2
                  ? `https://www.google.com/maps/search/?api=1&query=${
                      profData.ubicacion_geografica.coordinates[1]
                    },${profData.ubicacion_geografica.coordinates[0]}`
                  // si no, fallback por texto
                  : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                      profData.direccion
                    )}`
              }
              target="_blank"
              rel="noopener noreferrer"
            >
              <SlLocationPin className="icon" />
              <span className="info-text">{profData.direccion}</span>
            </a>
          )}
          {profData.horario && (
            <p className="icons-info">
              <TfiAlarmClock className="icon" />
              <span className="info-text">{profData.horario}</span>
            </p>
          )}
        </div>

        {/* Botones sticky */}
        <div
          ref={buttonContainerRef}
          className={`modalProfButtonContainer ${isStuck ? 'stuck' : ''}`}
        >
          <div className="redesSociales">
            {profData.enlace && (
              <div className="redesSociales-text">
                <a
                  href={profData.enlace}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="web-link"
                >
                  Visita nuestra web
                </a>
              </div>
            )}
            <div className="redesSociales-icons">
              {profData.facebook && (
                <a href={profData.facebook} target="_blank" rel="noopener noreferrer">
                  <img src="/Facebook.png" alt="Facebook" className="social-icon" />
                </a>
              )}
              {profData.instagram && (
                <a href={profData.instagram} target="_blank" rel="noopener noreferrer">
                  <img src="/Instagram.png" alt="Instagram" className="social-icon" />
                </a>
              )}
              {profData.twitter && (
                <a href={profData.twitter} target="_blank" rel="noopener noreferrer">
                  <img src="/Twitter.png" alt="Twitter" className="social-icon" />
                </a>
              )}
              {profData.youtube && (
                <a href={profData.youtube} target="_blank" rel="noopener noreferrer">
                  <img src="/Youtube.png" alt="Youtube" className="social-icon" />
                </a>
              )}
            </div>
          </div>
          <div className="prof-buttons-row">
            <ActionButton label="Acoge"        url={profData.acoge}       onClick={handleButtonClick} />
            <ActionButton label="Apadrina"     url={profData.apadrina}    onClick={handleButtonClick} />
            <ActionButton label="Voluntariado" url={profData.voluntariado} onClick={handleButtonClick} />
            <ActionButton label="Dona"         url={profData.donaciones}   onClick={handleButtonClick} />
            <ActionButton label="Socios"       url={profData.socio}        onClick={handleButtonClick} />
          </div>
        </div>

        {/* Detalles adicionales */}
        <div className="modalProfDetails">
          {profData.aboutus && <p>{profData.aboutus}</p>}
        </div>
      </div>
    </div>
  );
}
